import { useMemo } from 'react';

import { z } from 'zod';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  FULL_INVENTORY_REQUIRED_FIELDS,
  useEmbryoFieldsEnabled,
} from '@/pages/embryo-app/components/EmbryoForm/full-inventory';
import { Donor, Semen, Sire } from '@/types.gql';
import { parseWallClock, toWallClockISO } from '@/utils/wallClock';

/**
 * Shallow reference to a GraphQL entity ({ id, name }). Embedding the full
 * generated GQL entity types in form schemas makes react-hook-form's
 * FieldPath<T> template-literal expansion recurse through the entire
 * (mutually recursive) entity graph, which explodes type-checking time.
 */
type GqlRef<T> = Pick<T, Extract<keyof T, 'id' | 'name' | 'code'>>;

const numberOrNull = z.number().nullable().optional();

const REQUIRED_FIELD_MESSAGES: Record<
  (typeof FULL_INVENTORY_REQUIRED_FIELDS)[number],
  string
> = {
  freshFrozen: 'Fresh/Frozen is required',
  ownerId: 'Owner is required',
  status: 'Status Emb is required',
};
const dateTransform = z
  .union([z.string(), z.date()])
  .transform((val) => {
    if (val instanceof Date) return toWallClockISO(val);
    return val;
  })
  .optional();

const embryoFields = {
  name: z.string().optional(),
  status: z.number().optional(),
  statusCode: z.number().optional(),
  freshFrozen: z.number().optional(),
  ownerId: z.string().optional(),
  billingOwnerId: z.string().optional(),
  implantBillingAccountId: z.string().optional(),
  implantBillingAccountName: z.string().optional(),
  programName: z.string().optional(),
  soldTo: z.string().optional(),
  strawDisclaimer: z.string().optional(),
  strawNumber: numberOrNull,
  sex: z.number().optional(),
  breedId: z.custom<any>().optional(),
  freshStrawNo: numberOrNull,
  embTag: z.string().optional(),
  embTagNumber: z.number().optional(),
  donor: z.custom<GqlRef<Donor>>().optional(),
  sire: z.custom<GqlRef<Sire>>().optional(),
  cane: z.string().optional(),
  stage: numberOrNull,
  grade: numberOrNull,
  storageLocationId: z.string().optional(),
  siteId: z.string().optional(),
  freezeMethodId: z.string().optional(),
  freezer: z.string().optional(),
  dtPlus: z.boolean().optional(),
  tt: z.boolean().optional(),
  freezeComment: z.string().optional(),
  export: numberOrNull,
  freezeTechId: z.string().optional(),
  recoveryFirmCode: z.string().optional(),
  freezeTime: z.string().optional(),
  creationFreezeDate: z.string().optional(),
  receivedOn: z.string().optional(),
  grader7Id: z.string().optional(),
  grader7Name: z.string().optional(),
  crackedZona: z.boolean().optional(),
  shipmentId: z.string().optional(),
  stateCode: z.number().optional(),
  lastAct: z.string().optional(),
  fertilizationId: z.string().optional(),
  flush: z.string().optional(),
  flushName: z.string().optional(),
  statusReason: z.string().optional(),
  cellId: z.string().optional(),
  donorAge: z.union([z.string(), z.number()]).optional(),
  cellLineName: z.string().optional(),
  dateUsed: z.string().optional(),
  group: z.number().optional(),
  flushIdNumber: z.number().optional(),
  origin: z.string().optional(),
  transferTech2: z.string().optional(),
  estrusSync: numberOrNull,
  togId: z.number().optional(),
  recId: z.string().optional(),
  flshId: z.string().optional(),
  thawByName: z.string().optional(),
  thawById: z.string().optional(),
  frozenStage: numberOrNull,
  quantity: numberOrNull,
  originalLocation: z.string().optional(),
  oldStorageLocationId: z.string().optional(),
  frozenGrade: numberOrNull,
  originalOwner: z.string().optional(),
  oldOwnerId: z.string().optional(),
  accountType: z.number().optional(),
  strawText: z.string().optional(),
  embryoName: z.string().optional(),
  estDueDate: z.string().optional(),
  recordId: z.string().optional(),
  recipOwner: z.string().optional(),
  recipOwnerId: z.string().optional(),
  probableDonorId: z.custom<any>().optional(),
  probableDonorFromPool: z.string().optional(),
  outsideEntryId: z.string().optional(),
  billStatusCrt: z.number().optional(),
  billStatusImp: z.number().optional(),
  recoveryFirmName: z.string().optional(),
  stopId: z.number().optional(),
  originalFreezeSite: z.string().optional(),
  shipToClient: z.string().optional(),
  dairyCompId: z.number().optional(),
  dairyCompEmbryoId: z.number().optional(),
  embryoPa: z.number().optional(),
  embryoStagingId: z.number().optional(),
  approvalNumberId: z.string().optional(),
  cellLineId: z.string().optional(),
  tempEmbryoId: z.string().optional(),
  semenId: z.custom<GqlRef<Semen>>().optional(),
  sireName: z.string().optional(),
  createdByName: z.string().optional(),
  creation: z.string().optional(),
  modifiedByName: z.string().optional(),
  modifiedOn: z.string().optional(),
  method: z.number().optional(),
  calfType: z.number().optional(),
  implantStatusCode: z.number().optional(),
  implantDate: dateTransform,
  gnrhUsedAtImplant: z.boolean().optional(),
  qual: numberOrNull,
  dist: z.union([z.string(), z.number()]).optional(),
  rightLeft: z.number().optional(),
  technician1Id: z.string().optional(),
  thawMethodId: z.number().optional(),
  clinicRanchHaulIn: z.number().optional(),
  abort: z.any(),
  lowQuality: z.boolean().optional(),
  additionalEmbryoImplant: z.boolean().optional(),
  clientRecipient: z.string().optional(),
  preBook: z.any(),
  transferFarmLocationId: z.string().optional(),
  loadById: z.string().optional(),
  heat1: z.string().optional(),
  proj: z.string().optional(),
  projd: z.string().optional(),
  comm: z.string().optional(),
  qcEg10: z.string().optional(),
  qcEg10Date: z.string().optional(),
  qcEmbHolding: z.string().optional(),
  qcEmbHoldingExpDate: z.string().optional(),
  qcTryPsInDate: z.string().optional(),
  qcSucroseDate: z.string().optional(),
  qcehmDate: z.string().optional(),
  qcfreezingComment: z.string().optional(),
  qcStrawLotNumber: z.string().optional(),
  qcVitStrawLotNumber: z.string().optional(),
  qc12WellPlates: z.string().optional(),
  statusComment: z.string().optional(),
  transferQuantity: numberOrNull,
  implantTierOf: numberOrNull,
  deyardId: z.string().optional(),
  transferOwnerId: z.string().optional(),
  transferSiteId: z.string().optional(),
  recipientOwnerId: z.string().optional(),
  loadDonorPartners: z.number().optional(),
  freezeOwnerId: z.string().optional(),
  freezeQuantity: numberOrNull,
  transComment: z.string().optional(),
  transferDate: dateTransform,

  holsteinPtanetMerit: numberOrNull,
  jerseyPtanetMerit: numberOrNull,
  tpi: numberOrNull,
  jpi: numberOrNull,
  profit: numberOrNull,
  ranchValue: numberOrNull,
  feeder: numberOrNull,
  ppapy: numberOrNull,
  betaCasein: z.string().optional(),
  kappaCasein: z.string().optional(),
  hornStatus: z.string().optional(),
  sireNetMerit: numberOrNull,
  sireCheeseMerit: numberOrNull,
  sireTpi: numberOrNull,
  sireJpi: numberOrNull,
  donorNetMerit: numberOrNull,
  donorCheeseMerit: numberOrNull,
  donorTpi: numberOrNull,
  donorJpi: numberOrNull,
  calfSex: numberOrNull,
  pc1Date: dateTransform,
  pc1Res: numberOrNull,
  pc2Date: dateTransform,
  pc2Res: numberOrNull,
  pc3Date: dateTransform,
  pc3Res: numberOrNull,
  pc4Date: dateTransform,
  pc4Res: numberOrNull,
  pc5Date: dateTransform,
  pc5Res: numberOrNull,
  pc6Date: dateTransform,
  pc6Res: numberOrNull,
  pc7Date: dateTransform,
  pc7Res: numberOrNull,
  pc8Date: dateTransform,
  pc8Res: numberOrNull,
  pc9Date: dateTransform,
  pc9Res: numberOrNull,
  scHerdman: z.string().optional(),
  scHnDate: dateTransform,
  scIpDate: dateTransform,
  scRpDate: dateTransform,
  scPreviousHeat: dateTransform,
  scLastHeat: dateTransform,
  scSyDate: dateTransform,
  recipBreedId: z.custom<any>().optional(),
  recipBreedName: z.string().optional(),
  recipBirthDate: dateTransform,
  recipCalvingDate: dateTransform,
  recipRegNum: z.string().optional(),

  id: z.string().optional(),
  createdOn: z.string().optional(),
  createdOnBehalfById: z.string().optional(),
  createdOnBehalfByName: z.string().optional(),
  modifiedOnBehalfById: z.string().optional(),
  donorReg: z.string().optional(),
  donorBreedId: z.custom<any>().optional(),
  sireBreedId: z.custom<any>().optional(),
  naabCode: z.string().optional(),
  ownerCode: z.string().optional(),
  semenColCode: z.string().optional(),
  edonorId: z.string().optional(),
  straw: z.string().optional(),
  sync: z.string().optional(),
  grader65: z.string().optional(),
  label1: z.string().optional(),
  label2: z.string().optional(),
  storageLocationValidationId: z.string().optional(),
  exportTest1: z.string().optional(),
  exportTest2: z.string().optional(),
  exportTest3: z.string().optional(),
  qcGunWarmerTemp: z.string().optional(),
  qcSterileStrawLotNumber: z.string().optional(),
  qcStrawIncubatorNumber: z.string().optional(),
  qcThawThermosTemp: z.string().optional(),
  recipientId: z.string().optional(),
  recipBodyConditionScore: numberOrNull,
  recipCowHeifer: numberOrNull,
  recipOrderOfTransfer: numberOrNull,
  recipientParity: numberOrNull,
  recipientServices: numberOrNull,
  impId: z.number().optional(),
  loadTime: z.string().optional(),
  transferTime: z.string().optional(),
  shipToFbd: z.string().optional(),
  shipCity: z.string().optional(),
  shipState: z.string().optional(),
  psi: numberOrNull,
};

export type EmbryoFormData = z.infer<z.ZodObject<typeof embryoFields>>;

export function useEmbryoValidationSchema({
  isOutsideEmbryo = false,
  isBulkMode = false,
}: { isOutsideEmbryo?: boolean; isBulkMode?: boolean } = {}) {
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const finalEmbryoDatapoints = useFeatureFlag('Final_Embryo_Datapoints');
  const bulkOwnerOptionalEnabled = useFeatureFlag('Embryo_Bulk_Owner_Optional');
  const fullInventoryRequired = finalEmbryoDatapoints && !isOutsideEmbryo;
  const ownerOptional = isBulkMode && bulkOwnerOptionalEnabled;

  return useMemo(
    () =>
      z.object(embryoFields).superRefine((data, ctx) => {
        if (data.status === 2) {
          if (!data.calfType) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Implant Type is required',
              path: ['calfType'],
            });
          }
          if (!data.implantDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Implant Date is required',
              path: ['implantDate'],
            });
          }
          if (!addRemoveEmbryoFields && !data.clientRecipient) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Recip Tag is required',
              path: ['clientRecipient'],
            });
          }
          if (!data.clinicRanchHaulIn) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Clinic/Ranch/Haul-In is required',
              path: ['clinicRanchHaulIn'],
            });
          }
          if (!data.siteId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Site is required',
              path: ['siteId'],
            });
          }
        }

        if (data.implantDate) {
          const implantDate = parseWallClock(data.implantDate)!;
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          if (implantDate > today) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Implant date cannot be in the future',
              path: ['implantDate'],
            });
          }
        }

        if (fullInventoryRequired) {
          for (const field of FULL_INVENTORY_REQUIRED_FIELDS) {
            if (ownerOptional && field === 'ownerId') continue;
            const value = data[field as keyof typeof data];
            if (value === null || value === undefined || value === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: REQUIRED_FIELD_MESSAGES[field],
                path: [field],
              });
            }
          }
        } else if (addRemoveEmbryoFields) {
          const universalRequired: { field: string; message: string }[] = [
            { field: 'name', message: 'Embryo Name is required' },
            { field: 'freshFrozen', message: 'Fresh/Frozen is required' },
            { field: 'ownerId', message: 'Owner is required' },
            { field: 'billingOwnerId', message: 'Billing Owner is required' },
            {
              field: 'storageLocationId',
              message: 'Storage Location is required',
            },
            { field: 'siteId', message: 'Site is required' },
            { field: 'donor', message: 'Donor is required' },
            { field: 'creation', message: 'Creation/Freeze Date is required' },
            { field: 'createdByName', message: 'Created By is required' },
          ];

          for (const { field, message } of universalRequired) {
            if (ownerOptional && field === 'ownerId') continue;
            const value = data[field as keyof typeof data];
            if (value === null || value === undefined || value === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
                path: [field],
              });
            }
          }

          if (data.freshFrozen === 1) {
            const frozenRequired: { field: string; message: string }[] = [
              { field: 'cane', message: 'Cane is required when frozen' },
              {
                field: 'freezeMethodId',
                message: 'Freeze Method is required when frozen',
              },
              {
                field: 'receivedOn',
                message: 'Received is required when frozen',
              },
            ];

            for (const { field, message } of frozenRequired) {
              const value = data[field as keyof typeof data];
              if (value === null || value === undefined || value === '') {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message,
                  path: [field],
                });
              }
            }
          }
        }
      }),
    [addRemoveEmbryoFields, fullInventoryRequired, ownerOptional],
  );
}
